import {
  Box,
  Button,
  HStack,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import {useAddressContext, useCustomerContext} from 'components/providers';
import {IModalProps, Modal} from 'components/shared';
import {useFormik} from 'formik';
import {useToast, useTranslation} from 'hooks';
import {FC, FormEvent, useRef, useState} from 'react';
import GTMService from 'services/ga4';
import {colors, modalCloseButtonStyles} from 'theme';
import {ICustomerAddress} from 'types';
import {addressValidationSchema} from 'utils/dataValidations';

import {Form, Map} from './components';

interface Props extends IModalProps {
  customerAddress?: ICustomerAddress;
  createNewAddress: (address: ICustomerAddress) => void;
  updateAddress: (address: ICustomerAddress) => void;
  setAddresses: (addresses: any) => void;
  removeCoordinates: () => void;
  forcePrivateBuilding?: boolean;
  isCourierExpress?: boolean;
}

export const AddAddressModal: FC<Props> = ({
  isOpen,
  onClose,
  customerAddress,
  createNewAddress,
  updateAddress,
  isCourierExpress,
}) => {
  const {t} = useTranslation();
  const toast = useToast();
  const [locationAddress, setLocationAddress] = useState(customerAddress?.address);
  const {mapAddressChanged} = useAddressContext();

  const isEditMode = !!customerAddress;

  const {customer} = useCustomerContext();

  const formik = useFormik({
    initialValues: {
      apartment: customerAddress?.address.apartment ?? '',
      entrance: customerAddress?.address.entrance,
      floor: customerAddress?.address.floor,
      entrance_code: customerAddress?.address.entrance_code,
      comment: customerAddress?.customer_address_comment,
      address_type: 'home',
    },
    validateOnMount: true,
    validationSchema: addressValidationSchema(t),
    onSubmit: async (values) => {
      const newAddress = {
        // ...address,
        address_type: values.address_type,
        address: {
          ...locationAddress,
          entrance_code: values.entrance_code,
          comment: values.comment,
        },
        is_main: true,
      };

      newAddress.address.apartment = values.apartment;
      if (values.entrance) newAddress.address.entrance = values.entrance;
      if (values.floor) newAddress.address.floor = values.floor;
      newAddress.address.is_private_building = values.apartment ? false : true;

      try {
        customerAddress?.id
          ? updateAddress(newAddress as ICustomerAddress)
          : createNewAddress(newAddress as ICustomerAddress);

        GTMService.getAddress(customer, newAddress as ICustomerAddress);
        onClose();
      } catch (error) {}
    },
  });

  const isSubmitDisabled = !locationAddress || mapAddressChanged;
  const modalBodyRef = useRef<HTMLDivElement>(null);

  return (
    <Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={onClose}>
      <ModalContent
        overflow="hidden"
        maxW="864px"
        mx={['10px', 0]}
        maxH="calc(95vh - env(safe-area-inset-bottom))"
        my={['1vh', '5vh']}
      >
        <ModalCloseButton zIndex={9} sx={modalCloseButtonStyles} />
        <ModalHeader p={['10px', '30px']}>
          <Box pt="20px">
            <Text fontSize="22px">{t('addAddressTitle')}</Text>
            <Text fontSize="14px" fontWeight="medium">
              {t('addAddressDescription')}
            </Text>
          </Box>
        </ModalHeader>
        <ModalBody ref={modalBodyRef} p={0} overflowY="scroll" borderTopRadius="16px">
          <VStack spacing="0" w="full">
            <Map
              locationAddress={locationAddress}
              onLocationChange={(newLocation) => {
                if (
                  locationAddress?.latitude !== newLocation?.latitude ||
                  locationAddress?.longitude !== newLocation?.longitude
                ) {
                  formik.setFieldValue('apartment', '');
                  formik.setFieldValue('entrance', '');
                  formik.setFieldValue('floor', '');
                  formik.setFieldValue('entrance_code', '');
                }
                setLocationAddress(newLocation);
              }}
              isCourierExpress={isCourierExpress}
            />
            <Box
              as="form"
              p={['20px', '30px']}
              w="full"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                formik.submitForm();
              }}
            >
              <Form
                onChange={(key: string, value: any) => formik.setFieldValue(key, value)}
                values={formik.values}
                errors={formik.errors}
              />
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter
          p={['10px', '30px']}
          boxShadow="footer"
          flexDirection={['column', 'row']}
          alignItems="center"
          justifyContent="space-between"
        >
          <Box w="100%">
            <Tooltip
              label={isSubmitDisabled ? t('addressNotSelected') : ''}
              shouldWrapChildren
              placement="top"
              bg={colors.textGray}
              borderRadius="4px"
            >
              {isEditMode ? (
                <HStack spacing="15px" w="full">
                  <Button onClick={() => formik.submitForm()} variant="primary" flex="1" disabled={isSubmitDisabled}>
                    {t('CHANGE_ADDRESS')}
                  </Button>
                </HStack>
              ) : (
                <Button
                  onClick={() => {
                    if (Object.keys(formik.errors).length) {
                      modalBodyRef?.current?.scrollTo({
                        top: modalBodyRef?.current?.scrollHeight / 2.4,
                        behavior: 'smooth',
                      });
                      return toast.error(t('COMPLETE_ALL_FIELDS'));
                    }
                    if (!locationAddress?.latitude || !locationAddress.longitude)
                      return toast.error(t('addressNotSelected'));
                    formik.submitForm();
                  }}
                  variant="primary"
                  w="100%"
                  disabled={isSubmitDisabled}
                >
                  {t('SAVE_ADDRESS')}
                </Button>
              )}
            </Tooltip>
          </Box>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
